/**
 * Generates a natural sort key for a string by splitting it into text and numeric chunks.
 * Numbers are compared numerically (so "10" > "2"), text is compared case-insensitively,
 * and numeric chunks sort before text chunks (so "50-Series" < "A-Series").
 */
function naturalSortKey(value: string): Array<[number, number | string]> {
  if (!value) return [];
  return value
    .split(/(\d+)/)
    .filter(Boolean)
    .map((part) => (/^\d+$/.test(part) ? [0, Number(part)] : [1, part.toLowerCase()]));
}

/**
 * Compares two strings using natural sort order.
 * - Embedded digit sequences are treated as whole numbers ("Item-2" < "Item-10")
 * - Numbers sort before letters ("50-Series" < "A-Series")
 * - Case-insensitive ("b-1" and "B-2" sort together)
 */
function naturalCompare(a: string, b: string): number {
  const keysA = naturalSortKey(a);
  const keysB = naturalSortKey(b);
  const len = Math.max(keysA.length, keysB.length);

  for (let i = 0; i < len; i++) {
    const partA = keysA[i];
    const partB = keysB[i];

    // Shorter key comes first if all preceding parts are equal
    if (!partA) return -1;
    if (!partB) return 1;

    const [typeA, valA] = partA;
    const [typeB, valB] = partB;

    // Numbers (type 0) before letters (type 1)
    if (typeA !== typeB) return typeA - typeB;

    // Same type: compare values
    if (valA < valB) return -1;
    if (valA > valB) return 1;
  }

  return 0;
}

/**
 * Gets the value to order by from an item, either directly or from a specified key if the item is an object.
 */
function getItemToOrder<T extends string | number | Record<string, string | number | unknown>>(
  keyToOrder: string | undefined,
  item: T
): string | number | unknown {
  return keyToOrder && typeof item === 'object' && keyToOrder in item ? item[keyToOrder] : item;
}

/**
 * Sorts an array using natural sort order.
 * Handles arrays of strings/numbers or arrays of objects (with keyToOrder specifying the sort field).
 *
 * @param arr - The array to sort.
 * @param keyToOrder - Optional key to sort by if the array contains objects.
 * @returns The sorted array.
 */
const sortAlphanumericArrays = <T extends string | number | Record<string, string | number | unknown>>(
  arr: Array<T>,
  keyToOrder?: string
): T[] => {
  return [...arr].sort((a, b) => {
    const valA = String(getItemToOrder(keyToOrder, a) ?? '');
    const valB = String(getItemToOrder(keyToOrder, b) ?? '');
    return naturalCompare(valA, valB);
  });
};

/**
 * SEE-1325: orders coding options by the org's dropdown-sort choice.
 *
 * `byCode` sorts on the identifier (`code`/`number`); otherwise on the
 * name/description, which is what every ERP dropdown did before. Both use the
 * natural-sort rules above, so "104" follows "20" rather than preceding it.
 *
 * Only for lists fetched whole (the project-scoped cost code / cost type
 * queries). Paginated dropdowns must be ordered server-side — sorting a page
 * client-side only orders the rows already loaded, which is the SEE-346 defect
 * this ticket exists to fix.
 */
export const sortCodingOptions = <T extends Record<string, unknown>>(
  items: Array<T>,
  {
    byCode,
    codeField = 'code',
    nameField = 'name',
  }: { byCode: boolean; codeField?: string; nameField?: string }
): T[] => {
  // Blank sort values go LAST, matching the backend's code_sort_key /
  // name_sort_key. sortAlphanumericArrays coerces null/'' to the empty string,
  // whose sort key is empty — which sorts FIRST — so without this split a name
  // sort floats every blank-named row to the top. Vista equipment is where it
  // bites: many machines have a code and no description.
  const field = byCode ? codeField : nameField;
  const isBlank = (item: T) => {
    const value = item?.[field];
    return value === null || value === undefined || String(value).trim() === '';
  };
  const named = items.filter((item) => !isBlank(item));
  const blank = items.filter(isBlank);
  return [...sortAlphanumericArrays(named, field), ...blank];
};

/**
 * SEE-1325: the fields each coding entity orders on, mirroring the `code_field`
 * / `name_field` the resolvers pass to `_dropdown_sort.sort_rows`. Defined once
 * because three places sort these lists — the agaveSlice reducers (for the
 * filter dropdowns, bulk coding sheet, and advanced search), and
 * `useAgaveCodingDropdown` (for its local and search paths, which the slice
 * never sees). They must agree, or the same list orders differently depending
 * on which screen loaded it.
 *
 * Entities absent here are not ordered client-side.
 */
export const CODING_SORT_FIELDS: Partial<Record<string, { codeField: string; nameField: string }>> = {
  glCodes: { codeField: 'number', nameField: 'fullName' },
  projects: { codeField: 'number', nameField: 'fullName' },
  costCodes: { codeField: 'code', nameField: 'name' },
  costTypes: { codeField: 'code', nameField: 'name' },
  // Equipment and vendors were added later — equipment only became a coding
  // dropdown in SEE-1435, after this map was written. Without an entry here
  // `useAgaveCodingDropdown` returns early and they get no client-side ordering
  // in EITHER toggle state, which is why equipment alone came back in raw
  // DynamoDB order while the four above were alphabetical.
  //
  // Vendors order on `code` (Vista's vendor number, e.g. 32860) — there is no
  // `number` attribute on AgaveVendor, unlike GL codes and projects. `nameField`
  // is 'name' for the same reason: AgaveVendor has no `fullName` either, and
  // `getItemToOrder` falls back to the whole object when the key is absent, so
  // every comparison collapses to "[object Object]" and the sort silently
  // becomes a no-op.
  //
  // Equipment follows the toggle like everything else (product call, SEE-1325):
  // name order off, code order on. SEE-1435 had pinned it to code because a
  // name sort floated its many blank-named records to the top — that concern is
  // handled by sortCodingOptions putting blanks LAST, matching the backend.
  equipment: { codeField: 'code', nameField: 'name' },
  vendors: { codeField: 'code', nameField: 'name' },
  // SEE-1325: the EM lists. Server-side ordered by the resolvers, but the slice
  // re-sorts search and load-more results, so these must agree with the backend's
  // code_field / name_field or scrolling reorders what page 1 already ordered.
  emCostCodes: { codeField: 'code', nameField: 'name' },
  emCostTypes: { codeField: 'code', nameField: 'name' },
  // SEE-1622: sub-jobs (direct Sage 300 stores the visible code in `code`;
  // `number` is empty), departments and sub-accounts are code-ordered with the
  // toggle on, and employees by their ERP employee id (`source_id`, Intacct's
  // EMPLOYEEID). Locations are server-ordered too, but have no code, so the
  // toggle never reorders them and they have no entry here.
  subJobs: { codeField: 'code', nameField: 'name' },
  departments: { codeField: 'code', nameField: 'name' },
  subaccounts: { codeField: 'code', nameField: 'name' },
  employees: { codeField: 'source_id', nameField: 'name' },
};

export default sortAlphanumericArrays;
